import { lazy, ComponentType, LazyExoticComponent } from 'react';
import * as Sentry from '@sentry/react';

/**
 * Cria um lazy component com retry automático em caso de falha no carregamento.
 *
 * Estratégia de recovery (cumulativa via query param `_recovery`):
 *  - Sem `_recovery`: 1ª falha → reload mesma URL com `_cb` + `_recovery=1`.
 *  - `_recovery=1`: 2ª falha → redirect para `/` com `_cb` + `_recovery=2` (chunks diferentes).
 *  - `_recovery=2`: desiste e propaga erro pra ErrorBoundary mostrar modal.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retries = 3,
  delay = 1000
): LazyExoticComponent<T> {
  return lazy(async () => {
    let lastError: Error | null = null;

    const isChunkLoadError = (e: any) =>
      typeof e?.message === 'string' && (
        e.message.includes('Failed to fetch dynamically imported module') ||
        e.message.includes('Importing a module script failed') ||
        e.message.includes('error loading dynamically imported module')
      );

    // Extrai URL do .js do erro pra forçar re-fetch sem cache do navegador
    // (Vercel devolve 404 com `immutable, max-age=1y` durante janelas de deploy,
    // e o navegador acaba cacheando esse 404 — corrige a URL específica).
    const extractChunkUrl = (e: any): string | null => {
      const m = typeof e?.message === 'string'
        ? e.message.match(/https?:\/\/[^\s'"]+\.m?js/)
        : null;
      return m ? m[0] : null;
    };

    // Limpa params de recovery da URL atual sem disparar navegação (carrega OK
    // mas a aba ficou com `?_cb=...&_recovery=1` visível).
    const cleanRecoveryParamsFromUrl = () => {
      if (typeof window === 'undefined') return;
      try {
        const u = new URL(window.location.href);
        if (u.searchParams.has('_recovery') || u.searchParams.has('_cb')) {
          u.searchParams.delete('_recovery');
          u.searchParams.delete('_cb');
          window.history.replaceState(null, '', u.toString());
        }
      } catch {
        // ignora
      }
    };

    for (let i = 0; i < retries; i++) {
      try {
        const module = await importFn();
        cleanRecoveryParamsFromUrl();
        return module;
      } catch (error) {
        lastError = error as Error;
        console.warn(`Falha ao carregar módulo (tentativa ${i + 1}/${retries}):`, error);

        // Chunk error: tenta refrescar o cache do browser pra essa URL e
        // re-importar antes de cair pro fluxo de reload geral.
        if (isChunkLoadError(error)) {
          const chunkUrl = extractChunkUrl(error);
          if (chunkUrl && typeof window !== 'undefined') {
            try {
              await fetch(chunkUrl, { cache: 'reload', credentials: 'omit', mode: 'cors' });
              const module = await importFn();
              cleanRecoveryParamsFromUrl();
              return module;
            } catch {
              // ignora — vai cair no fluxo de redirect abaixo
            }
          }
          break;
        }

        // Se não for a última tentativa, aguarda antes de tentar novamente
        if (i < retries - 1) {
          await new Promise(resolve => setTimeout(resolve, delay * (i + 1)));

          // Tenta limpar o cache do módulo forçando um reload
          if (typeof window !== 'undefined' && 'caches' in window) {
            try {
              const cacheNames = await caches.keys();
              await Promise.all(
                cacheNames.map(name => caches.delete(name))
              );
            } catch (cacheError) {
              console.warn('Erro ao limpar cache:', cacheError);
            }
          }
        }
      }
    }

    console.error('Todas as tentativas de carregar o módulo falharam:', lastError);

    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      const recoveryStep = Number(url.searchParams.get('_recovery') || '0');
      const chunkUrl = extractChunkUrl(lastError);

      // Telemetria pré-redirect — mesmo se o reload nunca chegar de volta,
      // capturamos contexto pro Sentry.
      try {
        const swCount = 'serviceWorker' in navigator
          ? (await navigator.serviceWorker.getRegistrations()).length
          : 0;
        const cacheNames = 'caches' in window ? await caches.keys() : [];
        Sentry.captureMessage('chunk_load_failure', {
          level: 'warning',
          tags: {
            chunk_load_failure: true,
            chunk_url: chunkUrl || 'unknown',
            recovery_step: String(recoveryStep),
          },
          extra: {
            href: window.location.href,
            sw_registrations: swCount,
            cache_storage: cacheNames,
            message: lastError?.message,
          },
        });
      } catch {
        // não bloqueia o recovery se telemetria falhar
      }

      if (recoveryStep < 2) {
        // Limpeza agressiva antes do redirect: Caches API + service workers.
        // Defesa contra tabs antigas com SW pré-existente ou caches presos.
        try {
          if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map((name) => caches.delete(name)));
          }
        } catch (e) {
          console.warn('Falha ao limpar Caches API:', e);
        }
        try {
          if ('serviceWorker' in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map((r) => r.unregister()));
          }
        } catch (e) {
          console.warn('Falha ao desregistrar service workers:', e);
        }

        await new Promise<never>((_, reject) => {
          setTimeout(() => {
            let targetUrl: URL;
            if (recoveryStep === 0) {
              // 1ª falha: mesma URL com cache-bust + marcador de tentativa
              targetUrl = new URL(window.location.href);
              targetUrl.searchParams.set('_cb', Date.now().toString());
              targetUrl.searchParams.set('_recovery', '1');
            } else {
              // 2ª falha (recoveryStep === 1): vai pra `/` pra forçar chunks
              // diferentes (Index/Auth/Dashboard) e quebrar loop de rota específica.
              targetUrl = new URL('/', window.location.origin);
              targetUrl.searchParams.set('_cb', Date.now().toString());
              targetUrl.searchParams.set('_recovery', '2');
            }
            window.location.replace(targetUrl.toString());
            setTimeout(() => reject(lastError), 5000);
          }, 250);
        });
      }
      // recoveryStep === 2: já tentamos tudo, deixa o ErrorBoundary mostrar modal.
    }

    throw lastError || new Error('Erro desconhecido ao carregar módulo');
  });
}

